import { getFunctions } from 'firebase/functions'
import { app } from '@/lib/firebase/app'
import { createCallable } from '@/lib/firebase/functions'
import { formatUserFacingError, getErrorCode, withErrorCode } from '@/lib/errors'
import type {
  ParticipantCheckoutHandoff,
  ParticipantCheckoutRequest,
  ParticipantConfirmationContext,
  ParticipantConfirmationRequest,
  ParticipantPaymentCallableRequest,
  ParticipantPaymentCallableResponse,
  ParticipantResponseRequest,
  ParticipantResponseResult,
  ParticipantSelectionUpdateRequest,
  ParticipantSelectionUpdateResult,
  ParticipantWithdrawalRequest,
  ParticipantWithdrawalResult,
} from '../types'

const functions = getFunctions(app)

/**
 * Existing callable, enriched by the participant-package backend contract.
 * Keeping this wrapper local prevents the public token flow from widening the
 * shared functions.ts types before its server migration lands.
 */
const getContextFn = createCallable<
  ParticipantConfirmationRequest,
  ParticipantConfirmationContext
>(functions, 'getProductionConfirmationContext')

/** Accepts or declines an invitation and, for acceptance, reserves inventory. */
const respondFn = createCallable<ParticipantResponseRequest, ParticipantResponseResult>(
  functions,
  'confirmProductionResponse'
)

/** Atomically swaps the reserved performances before payment. */
const updateSelectionFn = createCallable<
  ParticipantResponseRequest,
  ParticipantResponseResult
>(functions, 'confirmProductionResponse')

/** Creates one server-owned participant-package checkout attempt. */
const createCheckoutFn = createCallable<
  ParticipantPaymentCallableRequest,
  ParticipantPaymentCallableResponse
>(functions, 'createParticipantPayment')

export const participantPackageService = {
  async getConfirmationContext(
    request: ParticipantConfirmationRequest
  ): Promise<ParticipantConfirmationContext> {
    const result = await getContextFn(request)
    return result.data
  },

  async respond(request: ParticipantResponseRequest): Promise<ParticipantResponseResult> {
    const result = await respondFn(request)
    return result.data
  },

  async updatePerformanceSelection(
    request: ParticipantSelectionUpdateRequest
  ): Promise<ParticipantSelectionUpdateResult> {
    const result = await updateSelectionFn({
      ...request,
      response: 'accept',
      acknowledged: true,
    })
    return { ok: true, context: result.data.context }
  },

  async createCheckout(request: ParticipantCheckoutRequest): Promise<ParticipantCheckoutHandoff> {
    const result = await createCheckoutFn({
      productionSlug: request.productionSlug,
      submissionId: request.submissionId,
      token: request.token,
      paymentAttemptId: request.idempotencyKey,
      acknowledged: request.acknowledged,
      returnUrl: request.returnUrl,
    })
    const data = result.data
    return { mode: 'hosted', url: data.checkoutUrl, orderId: data.orderId }
  },

  async withdraw(request: ParticipantWithdrawalRequest): Promise<ParticipantWithdrawalResult> {
    const result = await respondFn({
      ...request,
      response: 'withdraw',
    })
    return { ok: true, offerStatus: 'withdrawn', context: result.data.context }
  },
}

/** Convert Firebase callable failures into safe, participant-facing copy. */
export function humanizeParticipantPackageError(error: unknown): string {
  const candidate = error as { code?: string; message?: string } | null
  const errorCode = getErrorCode(error, 'participant-package/unknown')
  const code = errorCode.replace(/^functions\//, '')
  const message = candidate?.message?.toLowerCase() ?? ''

  if (code === 'permission-denied' || code === 'unauthenticated') {
    return withErrorCode(
      'This secure invitation link is invalid or has been revoked. Please use the latest link from your invitation email.',
      errorCode,
    )
  }
  if (code === 'not-found') {
    return withErrorCode('The invitation referenced by this link no longer exists.', errorCode)
  }
  if (code === 'deadline-exceeded' || code === 'cancelled' || message.includes('expired')) {
    return withErrorCode(
      'This invitation link has expired. Please contact the production team if you need help.',
      errorCode,
    )
  }
  if (code === 'resource-exhausted' || message.includes('capacity') || message.includes('sold out')) {
    return withErrorCode(
      'There is no longer enough performance capacity for this package. The production team has been notified.',
      errorCode,
    )
  }
  if (message.includes('cutoff') || message.includes('payment deadline')) {
    return withErrorCode('The payment window for this invitation has closed.', errorCode)
  }
  if (message.includes('processing') || message.includes('being confirmed')) {
    return withErrorCode(
      'Your payment is still being confirmed. Please wait a moment and refresh this page.',
      errorCode,
    )
  }
  if (message.includes('payment')) {
    return withErrorCode(
      'We could not start payment. Please try again, or contact the production team if the problem continues.',
      errorCode,
    )
  }
  return formatUserFacingError(
    error,
    'Something went wrong. Please try again.',
    errorCode,
  )
}
